/**
 * Validation functions for backend metrics data structure
 */

import type { 
  BackendMetricsAccount, 
  BackendMetricsStrategy, 
  BackendMetricsPortfolio,
  BackendMetricsData 
} from '../types/metricsTypes';

/**
 * Validate incremental metrics data structure
 */
export const validateIncrementalMetricsData = (data: any): boolean => {
  try {
    console.log('🔍 Validating incremental metrics data structure:', {
      hasData: !!data,
      type: typeof data,
      hasUserId: typeof data?.user_id === 'string',
      hasCalculatedAt: typeof data?.calculated_at === 'number',
      updateType: data?.update_type,
      hasTradeData: !!data?.trade_data,
      hasPnlTimeframes: !!data?.pnl_timeframes,
    });
    
    const isValid = (
      data &&
      typeof data === 'object' &&
      typeof data.user_id === 'string' &&
      typeof data.calculated_at === 'number' &&
      data.update_type === 'incremental' &&
      data.trade_data &&
      typeof data.trade_data === 'object' &&
      data.pnl_timeframes &&
      typeof data.pnl_timeframes === 'object'
    );
    
    if (isValid) {
      console.log('✅ Incremental metrics data validation passed');
    } else {
      console.error('❌ Incremental metrics data validation failed');
    }
    
    return isValid;
  } catch (error) {
    console.error('Error validating incremental metrics data:', error);
    return false;
  }
};

/**
 * Validate complete metrics data structure
 */
export const validateMetricsData = (data: any): data is BackendMetricsData => {
  try {
    // Check if this is incremental data first
    if (data?.update_type === 'incremental') {
      return validateIncrementalMetricsData(data);
    }
    
    console.log('🔍 Validating full metrics data structure:', {
      hasData: !!data,
      type: typeof data,
      hasUserId: typeof data?.user_id === 'string',
      hasCalculatedAt: typeof data?.calculated_at === 'number',
      hasStrategies: Array.isArray(data?.strategies),
      hasAccounts: Array.isArray(data?.accounts),
      hasPortfolio: !!data?.portfolio,
      strategiesCount: data?.strategies?.length || 0,
      accountsCount: data?.accounts?.length || 0
    });
    
    const isValid = (
      data &&
      typeof data === 'object' &&
      typeof data.user_id === 'string' &&
      typeof data.calculated_at === 'number' &&
      Array.isArray(data.strategies) &&
      Array.isArray(data.accounts) &&
      data.portfolio &&
      typeof data.portfolio === 'object' &&
      validateAccountsArray(data.accounts) &&
      validateStrategiesArray(data.strategies) &&
      validatePortfolio(data.portfolio)
    );
    
    if (!isValid) {
      console.error('❌ Full metrics data validation failed');
      console.log('🔍 Detailed validation results:', {
        hasValidAccounts: validateAccountsArray(data?.accounts || []),
        hasValidStrategies: validateStrategiesArray(data?.strategies || []),
        hasValidPortfolio: validatePortfolio(data?.portfolio || {}),
        sampleAccount: data?.accounts?.[0],
        sampleStrategy: data?.strategies?.[0],
        portfolio: data?.portfolio
      });
    } else {
      console.log('✅ Full metrics data validation passed');
    }
    
    return isValid;
  } catch (error) {
    console.error('Error validating metrics data:', error);
    return false;
  }
};

/**
 * Validate accounts array structure
 */
export const validateAccountsArray = (accounts: any[]): accounts is BackendMetricsAccount[] => {
  if (!Array.isArray(accounts)) return false;
  
  return accounts.every(account => 
    account &&
    typeof account === 'object' &&
    typeof account.account_name === 'string' &&
    typeof account.total_pnl === 'number' &&
    typeof account.balance_usdt === 'number' &&
    typeof account.total_trades === 'number' &&
    typeof account.strategies_count === 'number' &&
    typeof account.last_activity === 'number'
  );
};

/**
 * Validate strategies array structure
 */
export const validateStrategiesArray = (strategies: any[]): strategies is BackendMetricsStrategy[] => {
  if (!Array.isArray(strategies)) return false;
  
  return strategies.every(strategy => 
    strategy &&
    typeof strategy === 'object' &&
    typeof strategy.strategy_name === 'string' &&
    typeof strategy.account_name === 'string' &&
    typeof strategy.total_pnl === 'number' &&
    typeof strategy.total_trades === 'number' &&
    typeof strategy.win_rate === 'number' &&
    ['active', 'recent', 'inactive'].includes(strategy.status)
  );
};

/**
 * Validate portfolio structure with timeframes
 */
export const validatePortfolio = (portfolio: any): portfolio is BackendMetricsPortfolio => {
  if (!portfolio || typeof portfolio !== 'object') return false;
  
  const basicValidation = (
    typeof portfolio.total_pnl === 'number' &&
    typeof portfolio.total_trades === 'number' &&
    typeof portfolio.total_balance === 'number' &&
    typeof portfolio.calculated_at === 'number'
  );
  
  // Check for timeframes (optional for backward compatibility)
  const hasTimeframes = portfolio.pnl_timeframes && 
    typeof portfolio.pnl_timeframes === 'object' &&
    typeof portfolio.pnl_timeframes.pnl_today === 'number' &&
    typeof portfolio.pnl_timeframes.pnl_yesterday === 'number' &&
    typeof portfolio.pnl_timeframes.pnl_last_7_days === 'number' &&
    typeof portfolio.pnl_timeframes.pnl_last_30_days === 'number' &&
    typeof portfolio.pnl_timeframes.pnl_last_90_days === 'number';
  
  if (!hasTimeframes) {
    console.warn('Portfolio missing pnl_timeframes - using compatibility mode');
  }
  
  return basicValidation;
};

/**
 * Validate JSON string before parsing
 */
export const validateJsonString = (jsonString: string): boolean => {
  try {
    JSON.parse(jsonString);
    return true;
  } catch (error) {
    console.error('Invalid JSON string:', error);
    return false;
  }
};

/**
 * Safe JSON parsing with validation (handles both full and incremental metrics)
 */
export const safeParseMetrics = (jsonString: string): BackendMetricsData | any | null => {
  try {
    if (!validateJsonString(jsonString)) {
      return null;
    }
    
    const parsed = JSON.parse(jsonString);
    
    // Handle incremental metrics separately (looser validation)
    if (parsed?.update_type === 'incremental') {
      if (validateIncrementalMetricsData(parsed)) {
        console.log('✅ Incremental metrics parsed successfully');
        return parsed;
      } else {
        console.error('❌ Incremental metrics validation failed');
        return null;
      }
    }
    
    // Handle full metrics with strict validation
    if (!validateMetricsData(parsed)) {
      console.error('Parsed data does not match expected metrics structure');
      return null;
    }
    
    return parsed;
  } catch (error) {
    console.error('Error parsing metrics JSON:', error);
    return null;
  }
};